/* ============================================================================
   MY BABY cart drawer behaviour.

   Loaded AFTER theme.js, deferred. Nothing in assets/theme.js or
   assets/theme.dev.js is edited: theme.js is the minified file the browser
   actually runs and theme.dev.js is only the readable source, so a change
   there has to land in both by hand. Everything below is done from outside,
   by listening to the theme's own events and patching two prototype methods.

   What this adds:
     1. Undo after a removal
     2. A live region that actually announces (the drawer never has)
     3. window.publish of PUB_SUB_EVENTS.cartUpdate, which the theme declares
        and has never once published, while two shipped features subscribe
     4. The generic cart event names third-party apps really ship
     5. open() / close() / toggle() aliases and window.theme.cartDrawer
     6. Focus continuity across the rebuild that destroys the focused control
     7. A guard for the updateCart dead end that bricks the drawer
     8. Re-applying [data-mb-keep-disabled] the theme force-enables
     9. The animated character over the checkout button: source chosen per
        engine, loaded on the first drawer open, images kept as the fallback
    10. The drawer opens from a cart rebuild only on the shopper's intent
    11. Visible or closed, never in between: [inert] while closed and a
        watchdog that settles a stalled open or close (contract 3 in
        cart-drawer.css)
    12. A new visitor's first adds go one at a time, so Shopify cannot
        create two carts and lose one of the products

   Full notes in CART-DRAWER-NOTES.md.
   ============================================================================ */
(function () {
  'use strict';

  var UNDO_MS = 6000;
  var undoTimer = null;
  var pendingUndo = null;

  function drawer() {
    return document.querySelector('cart-drawer');
  }
  function t(key, fallback) {
    var el = document.querySelector('[data-mbc-i18n="' + key + '"]');
    return (el && el.textContent.trim()) || fallback;
  }

  /* ---------------------------------------------------------------- 1. undo
     Captured on the way DOWN, because the click handler that removes the line
     also wipes the list, and with it the row we need to read the quantity from.
     The strip lives in the chrome for the same reason. */
  document.addEventListener(
    'click',
    function (event) {
      var control = event.target && event.target.closest && event.target.closest('[data-item-remove]');
      if (!control) return;

      var row = control.closest('[data-item]');
      if (!row) return;

      var field = row.querySelector('.cart__quantity-field, input[name="updates[]"]');
      /* The VARIANT id, not the line key. Once the line is gone its key no
         longer exists in the cart, so /cart/change.js cannot resurrect it and
         the undo has to be a re-add. */
      pendingUndo = {
        variantId: row.getAttribute('data-variant-id'),
        quantity: field ? parseInt(field.value, 10) || 1 : 1,
        properties: readProperties(row),
      };
      showUndo();
    },
    true
  );

  /* Line item properties are what an engraving or personalisation app writes,
     and they are part of what was removed. Read them back off the row so an
     undo restores the same line rather than a bare variant. */
  function readProperties(row) {
    var raw = row.getAttribute('data-line-properties');
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      /* an empty object would make add.js create a distinct line for no reason */
      return parsed && Object.keys(parsed).length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function showUndo() {
    var strip = document.querySelector('[data-cart-undo]');
    if (!strip) return;
    strip.hidden = false;
    clearTimeout(undoTimer);
    undoTimer = setTimeout(hideUndo, UNDO_MS);
  }

  function hideUndo() {
    var strip = document.querySelector('[data-cart-undo]');
    if (strip) strip.hidden = true;
    pendingUndo = null;
    clearTimeout(undoTimer);
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('[data-cart-undo-btn]');
    if (!btn || !pendingUndo) return;
    event.preventDefault();

    var restore = pendingUndo;
    hideUndo();

    if (!restore.variantId) return;

    var item = { id: Number(restore.variantId), quantity: restore.quantity };
    if (restore.properties) item.properties = restore.properties;

    fetch(window.theme.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      })
      .catch(function () {
        /* the line is simply not restored; the cart itself is untouched */
      });
  });

  /* the drawer closing should not leave a stale undo offer behind */
  document.addEventListener('theme:cart-drawer:close', hideUndo);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var t = event.target;
    if (!t || !t.closest) return;
    /* the theme's remove-discount control is a span[role=button] with only a click listener */
    var rd = t.closest('[data-remove-discount]');
    if (rd) { event.preventDefault(); rd.click(); return; }
    /* a second activation of a trash link mid-request would post a stale line index */
    var rm = t.closest('[data-item-remove]');
    var host = drawer();
    if (rm && host && host.classList.contains('is-loading')) { event.preventDefault(); event.stopPropagation(); }
  }, true);

  /* ------------------------------------------- 2. live region + 3. pub/sub */
  function announce(cart) {
    var region = document.querySelector('[data-cart-live-region]');
    if (!region || !cart) return;
    var template = region.getAttribute('data-announce-template') || '';
    var text = template
      ? template.replace('{{ count }}', cart.item_count).replace('{{ amount }}', formatMoney(cart.total_price))
      : cart.item_count + ' / ' + formatMoney(cart.total_price);
    /* clear first so a repeat of the same string is still announced */
    region.textContent = '';
    setTimeout(function () {
      region.textContent = text;
    }, 60);
  }

  function formatMoney(cents) {
    try {
      if (window.theme && typeof window.theme.formatMoney === 'function' && window.theme.moneyFormat) {
        return String(window.theme.formatMoney(cents, window.theme.moneyFormat)).replace(/<[^>]+>/g, '');
      }
      if (window.theme && window.theme.moneyFormat) {
        return window.theme.moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2));
      }
    } catch (e) {}
    return (cents / 100).toFixed(2);
  }

  /* theme.dev.js:2243 rewrites [data-cart-total] after every change WITHOUT the <bdi> and in the
     with-currency format, so the subtotal changed format mid-session and lost its isolation.
     Put it back the way the Liquid first painted it. */
  function restoreSubtotal(cart) {
    var el = document.querySelector('[data-cart-total]');
    if (!el || !cart) return;
    var text = cart.total_price === 0 ? el.textContent.trim() : formatMoney(cart.total_price);
    if (!el.querySelector('bdi')) el.innerHTML = '<bdi>' + text + '</bdi>';
    else el.querySelector('bdi').textContent = text;
  }

  function onCartChanged() {
    fetch(window.theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        announce(cart);
        restoreSubtotal(cart);

        /* The theme declares PUB_SUB_EVENTS.cartUpdate and exports publish, but
           nothing has ever published it, while price-per-item.js and
           product-info.js both subscribe. Publishing lights both back up and
           gives Dawn-shaped apps a channel that actually fires. */
        try {
          if (window.publish && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.theme.PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-items',
              cartCount: cart.item_count,
              cartData: cart,
            });
          }
        } catch (e) {}

        restoreKeptDisabled();
        syncCountLabel(cart.item_count);
        mirrorDisabled();
        restoreFocus();
      })
      .catch(function () {});
  }

  function syncCountLabel(count) {
    var label = document.querySelector('[data-cart-count-label]');
    if (!label) return;
    var one = label.getAttribute('data-count-one') || '';
    var other = label.getAttribute('data-count-template') || '';
    if (count === 1 && one) { label.textContent = one; return; }
    if (other) label.textContent = other.replace('99999', String(count));
  }

  /* validateQtyRules toggles only the class `disabled` (theme.dev.js:580-591) */
  function mirrorDisabled() {
    document.querySelectorAll('cart-drawer .quantity__button').forEach(function (b) {
      var off = b.classList.contains('disabled');
      b.setAttribute('aria-disabled', off ? 'true' : 'false');
      b.tabIndex = off ? -1 : 0;
    });
  }
  var qtyObserver = new MutationObserver(function () { mirrorDisabled(); });
  function observeQty() {
    var host = document.querySelector('cart-drawer');
    if (host) qtyObserver.observe(host, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  document.addEventListener('theme:cart:change', onCartChanged);

  /* --------------------------------------- 4. the event names apps ship
     The theme's own hook is theme:cart:refresh, which no app will guess. */
  ['cart:refresh', 'cart:update', 'cart:build', 'updateCart'].forEach(function (name) {
    [document, document.documentElement].forEach(function (target) {
      target.addEventListener(name, function () {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      });
    });
  });

  /* ------------------------------------------- 5. a documented open/close API */
  function wireApi() {
    var el = drawer();
    if (!el) return;

    window.theme = window.theme || {};
    window.theme.cartDrawer = el;

    var proto = Object.getPrototypeOf(el);
    if (proto && !proto.__mbcAliased) {
      proto.__mbcAliased = true;
      if (typeof proto.openCartDrawer === 'function' && !proto.open) {
        proto.open = function () {
          return this.openCartDrawer.apply(this, arguments);
        };
      }
      if (typeof proto.closeCartDrawer === 'function' && !proto.close) {
        proto.close = function () {
          return this.closeCartDrawer.apply(this, arguments);
        };
      }
      if (typeof proto.toggleCartDrawer === 'function' && !proto.toggle) {
        proto.toggle = function () {
          return this.toggleCartDrawer.apply(this, arguments);
        };
      }
    }
  }

  /* neutral aliases beside the theme's own events, which keep firing unchanged */
  /* ------------------------------------------- 9. the mascot video
     One <video>, no src and no <source> in the markup. Chrome can play HEVC but
     drops its alpha; newer Safari can play VP9 but drops its alpha; so the file
     is chosen HERE, from the engine, never by <source> order. WebKit that is not
     Chrome/Chromium/Edge/Opera/Android gets the HEVC-alpha mp4; everything else
     gets the VP9-alpha webm. Assigned on the first drawer open, client side, so
     nothing is fetched on the page views that never open the cart, and so the
     Liquid markup is identical to what the browser first sees.

     TWO DISPLAY MODES. Non-WebKit shows the <video> element itself. WebKit shows
     a <canvas> that receives the video's frames every animation frame, because
     on iOS the on-screen video layer composited the HEVC-alpha clip as opaque
     white while a canvas readback of the very same frames had the alpha intact.
     A canvas is an ordinary element and composites like one.

     In both modes nothing is revealed until a drawn frame PROVES the alpha: the
     clip is cropped tightly, so its corners are alpha 0 and its centre is the
     character. Corners opaque means the engine dropped the alpha; centre empty
     means no real frame yet. Needs crossorigin="anonymous" (Shopify's CDN sends
     Access-Control-Allow-Origin: *). A tainted canvas is 'unknown': WebKit falls
     back, the rest reveal the video, because the webm path is proven.

     The two static images stay in the DOM: poster, what shows until the reveal,
     what prefers-reduced-motion users get, and what comes back on any failure.
     For testing: localStorage.mbcMascot = 'canvas' | 'video' forces a mode. */
  var mascotVideo = null;
  var mascotCanvas = null;
  var mascotMode = 'video';   /* 'video' | 'canvas' */
  var mascotState = 'idle';   /* idle | loading | playing | fallback */
  var mascotRaf = 0;

  function isWebKitEngine() {
    var ua = navigator.userAgent;
    return /AppleWebKit/.test(ua) && !/Chrome\/|Chromium|Edg\/|OPR\/|Android/.test(ua);
  }

  function mascotHost() { return document.querySelector('cart-drawer .cart__buttons'); }

  function mascotFallback() {
    if (mascotRaf) { cancelAnimationFrame(mascotRaf); mascotRaf = 0; }
    var host = mascotHost();
    if (host) host.removeAttribute('data-mascot');
    if (mascotVideo) {
      try { mascotVideo.pause(); } catch (e) {}
      mascotVideo.removeAttribute('src');
      try { mascotVideo.load(); } catch (e) {}
    }
    mascotState = 'fallback';
  }

  /* 'ok' | 'blank' | 'opaque' | 'unknown' from a 2d context holding one frame */
  function mascotVerdict(ctx, w, h) {
    try {
      var centre = ctx.getImageData(Math.round(w / 2), Math.round(h / 2), 1, 1).data[3];
      if (centre < 200) return 'blank';
      var pts = [[1, 1], [w - 2, 1], [1, h - 2], [w - 2, h - 2]];
      var opaque = 0;
      for (var i = 0; i < pts.length; i++) if (ctx.getImageData(pts[i][0], pts[i][1], 1, 1).data[3] > 250) opaque++;
      return opaque === pts.length ? 'opaque' : 'ok';
    } catch (e) {
      return 'unknown'; /* SecurityError: tainted canvas */
    }
  }

  /* video mode: draw one frame into a scratch canvas just to judge it */
  function mascotJudgeVideo(v) {
    if (!v.videoWidth || !v.videoHeight) return 'blank';
    var c = document.createElement('canvas');
    c.width = 32; c.height = 32;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return 'unknown';
    try { ctx.drawImage(v, 0, 0, 32, 32); } catch (e) { return 'unknown'; }
    return mascotVerdict(ctx, 32, 32);
  }

  function mascotRevealVideo(v, tries) {
    var verdict = mascotJudgeVideo(v);
    if (verdict === 'blank' && tries < 40) { setTimeout(function () { mascotRevealVideo(v, tries + 1); }, 50); return; }
    if (verdict === 'opaque' || verdict === 'blank' || (verdict === 'unknown' && isWebKitEngine())) { mascotFallback(); return; }
    var host = mascotHost();
    if (host) host.setAttribute('data-mascot', 'video');
    mascotState = 'playing';
  }

  /* canvas mode: paint every frame; reveal once a frame proves the alpha */
  function mascotCanvasLoop(v) {
    var c = mascotCanvas;
    if (!c) { mascotFallback(); return; }
    var w = c.width, h = c.height;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) { mascotFallback(); return; }
    var verified = false, frames = 0;
    var tick = function () {
      mascotRaf = 0;
      if (mascotState === 'fallback') return;
      if (!v.paused && !v.ended && v.readyState >= 2) {
        ctx.clearRect(0, 0, w, h);
        try { ctx.drawImage(v, 0, 0, w, h); } catch (e) { mascotFallback(); return; }
        if (!verified) {
          frames++;
          var verdict = mascotVerdict(ctx, w, h);
          if (verdict === 'ok') {
            verified = true;
            var host = mascotHost();
            if (host) host.setAttribute('data-mascot', 'canvas');
            mascotState = 'playing';
          } else if (verdict === 'opaque' || verdict === 'unknown' || frames > 60) {
            mascotFallback(); return;
          }
        }
      }
      mascotRaf = requestAnimationFrame(tick);
    };
    mascotRaf = requestAnimationFrame(tick);
  }

  function startMascot() {
    if (mascotState === 'playing' && mascotVideo) {
      mascotVideo.play().catch(function () {});
      if (mascotMode === 'canvas' && !mascotRaf) mascotCanvasLoop(mascotVideo);
      return;
    }
    if (mascotState !== 'idle') return;

    var v = document.querySelector('cart-drawer [data-mascot-video]');
    if (!v) return;

    /* reduced motion, or a panel too narrow to show the character at all: never load it */
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { mascotState = 'fallback'; return; }
      if (!window.matchMedia('(min-width: 380px)').matches) { mascotState = 'fallback'; return; }
    }

    var webkit = isWebKitEngine();
    var src = v.getAttribute(webkit ? 'data-src-mp4' : 'data-src-webm');
    if (!src) { mascotState = 'fallback'; return; }

    var forced = null;
    try { forced = window.localStorage.getItem('mbcMascot'); } catch (e) {}
    mascotMode = forced === 'canvas' || forced === 'video' ? forced : (webkit ? 'canvas' : 'video');
    mascotCanvas = document.querySelector('cart-drawer [data-mascot-canvas]');
    if (mascotMode === 'canvas' && !mascotCanvas) mascotMode = 'video';

    mascotVideo = v;
    mascotState = 'loading';
    v.muted = true;
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('webkit-playsinline', '');
    /* an in-app browser that refuses inline playback opens play() full screen: leave it and show the still */
    v.addEventListener('webkitbeginfullscreen', function () {
      try { v.webkitExitFullscreen(); } catch (e) {}
      mascotFallback();
    });

    v.addEventListener('error', mascotFallback);
    v.addEventListener('playing', function () {
      if (mascotMode === 'canvas') mascotCanvasLoop(v);
      else if (typeof v.requestVideoFrameCallback === 'function') v.requestVideoFrameCallback(function () { mascotRevealVideo(v, 0); });
      else setTimeout(function () { mascotRevealVideo(v, 0); }, 120);
    }, { once: true });

    v.src = src;
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(mascotFallback);
  }

  function pauseMascot() {
    if (mascotRaf) { cancelAnimationFrame(mascotRaf); mascotRaf = 0; }
    if (mascotVideo && mascotState === 'playing') {
      try { mascotVideo.pause(); } catch (e) {}
    }
  }

  /* [inert] as well as aria-hidden: a closed drawer takes no taps, no focus and
     no scroll gestures, whatever a block inside it writes on itself. See
     contract 3 in cart-drawer.css. */
  function syncDialog(open) {
    var el = drawer();
    if (!el) return;
    if (open) {
      el.removeAttribute('aria-hidden');
      el.setAttribute('aria-modal', 'true');
      el.removeAttribute('inert');
    } else {
      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('aria-modal');
      el.setAttribute('inert', '');
    }
  }

  /* ------------------------------------------- native scroll feel
     vendor.js (scroll-lock) listens to touchmove on the document while the page is
     locked and cancels any move that would push a scroller past its edge: its guard
     against the page rubber-banding behind a drawer. That guard is what makes the
     cart stop dead at the top and bottom instead of bouncing like an app. Every
     scroller in this drawer carries overscroll-behavior: contain, so the browser
     itself already refuses to hand the scroll on to the page; the guard buys nothing
     here and costs the bounce. So a touch inside a drawer scroller that actually
     overflows stops at the drawer and never reaches the library, and the browser
     scrolls and bounces it natively. Touches anywhere else still reach the library,
     which keeps the page pinned as before. Passive: this only stops propagation. */
  function wireNativeScroll() {
    var el = drawer();
    if (!el || el.hasAttribute('data-mbc-native-scroll')) return;
    el.setAttribute('data-mbc-native-scroll', '');
    el.addEventListener('touchmove', function (event) {
      var t = event.target;
      if (!t || !t.closest) return;
      var sc = t.closest('[data-scroll-lock-scrollable]');
      if (!sc || !el.contains(sc)) return;
      if (sc.scrollHeight > sc.clientHeight + 1) event.stopPropagation();
    }, { passive: true });
  }
  wireNativeScroll();

  /* ------------------------------------------- follow an opening widget
     theme.dev.js CollapsibleElements animates a widget's height (gift note, discount,
     order note) but leaves the list where it was, so "Is this a gift?" opens below the
     fold and the shopper sees nothing happen. While the height transition runs, keep
     the widget's bottom edge inside its scroller frame by frame, and never push the
     widget's own title out of the top. The event fires the moment the height is set,
     so the loop runs for the transition's duration plus a little slack. */
  document.addEventListener('theme:collapsible:toggle', function (event) {
    var col = event.target;
    if (!col || !col.closest || !col.closest('cart-drawer')) return;
    if (col.getAttribute('open') !== 'true') return;
    var body = col.querySelector('[data-collapsible-body]');
    var scroller = col.parentElement;
    while (scroller && scroller !== document.body) {
      if (/auto|scroll/.test(getComputedStyle(scroller).overflowY)) break;
      scroller = scroller.parentElement;
    }
    if (!scroller || scroller === document.body) return;
    var dur = 0;
    try { dur = (parseFloat(getComputedStyle(body).transitionDuration) || 0) * 1000; } catch (e) {}
    var end = performance.now() + Math.max(dur, 50) + 140;
    var follow = function () {
      var sr = scroller.getBoundingClientRect(), cr = col.getBoundingClientRect();
      var over = cr.bottom - sr.bottom;
      var room = cr.top - sr.top;
      if (over > 0 && room > 0) scroller.scrollTop += Math.min(over, room);
      if (performance.now() < end) requestAnimationFrame(follow);
    };
    requestAnimationFrame(follow);
  });

  document.addEventListener('theme:cart-drawer:open', function (e) {
    document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true, detail: e.detail }));
    syncToggles(true);
    syncDialog(true);
    wireNativeScroll();
    startMascot();
  });
  document.addEventListener('theme:cart-drawer:close', function (e) {
    document.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true, detail: e.detail }));
    syncToggles(false);
    syncDialog(false);
    pauseMascot();
  });

  function syncToggles(open) {
    document.querySelectorAll('[data-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ------------------------------------------- 6. focus continuity
     disableCartButtons() blurs the focused control and the items-holder
     innerHTML swap then destroys it, so focus lands on body after every
     quantity change and every removal. A dialog role over a trap that drops
     focus on every interaction is worse than no role at all. */
  var focusHint = null;

  document.addEventListener(
    'click',
    function (event) {
      var el = event.target && event.target.closest && event.target.closest('[data-item] button, [data-item] a');
      if (!el) return;
      var row = el.closest('[data-item]');
      focusHint = {
        index: row ? parseInt(row.getAttribute('data-item-index'), 10) : null,
        removing: !!el.closest('[data-item-remove]'),
        name: el.getAttribute('name') || null,
      };
    },
    true
  );

  function restoreFocus() {
    var el = drawer();
    if (!el || !el.classList.contains('is-open') || !focusHint) return;
    var hint = focusHint;
    focusHint = null;

    var target = null;
    if (hint.index) {
      var rows = el.querySelectorAll('[data-item]');
      /* the row that took the removed row's place, else the last row */
      var wanted = hint.removing ? hint.index : hint.index;
      target = rows[Math.min(wanted - 1, rows.length - 1)] || null;
      if (target && !hint.removing && hint.name) {
        target = target.querySelector('[name="' + hint.name + '"]') || target;
      }
    }
    if (!target) target = el.querySelector('[data-cart-total]');
    if (!target) return;

    if (!target.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) {
      target.setAttribute('tabindex', '-1');
    }
    try {
      target.focus({ preventScroll: true });
    } catch (e) {}

    /* re-arm the trap: first and last were snapshotted at open time */
    try {
      var inner = el.querySelector('[data-cart-drawer-inner]');
      if (window.a11y && window.a11y.trapFocus && inner) window.a11y.trapFocus(inner, { elementToFocus: target });
    } catch (e) {}
  }

  /* ------------------------------------------- 7. the updateCart dead end
     updateCart() calls disableCartButtons() then returns early when lineIndex
     is 0, with no re-enable and no error, leaving every input and button in the
     drawer disabled with no recovery short of a page reload. That is exactly
     the state you land in when an app has mutated the cart behind the theme's
     back. */
  function patchUpdateCart() {
    var el = drawer();
    if (!el) return;
    var items = document.querySelector('cart-items');
    if (!items) return;
    var proto = Object.getPrototypeOf(items);
    if (!proto || proto.__mbcGuarded || typeof proto.updateCart !== 'function') return;

    proto.__mbcGuarded = true;
    var original = proto.updateCart;
    proto.updateCart = function (updateData, currentItem) {
      var index = currentItem && currentItem.getAttribute && currentItem.getAttribute('data-item-index');
      if (currentItem && (!index || parseInt(index, 10) <= 0)) {
        try {
          if (typeof this.enableCartButtons === 'function') this.enableCartButtons();
          if (typeof this.getCart === 'function') this.getCart();
        } catch (e) {}
        return;
      }
      return original.apply(this, arguments);
    };
  }

  /* ------------------------------------------- 8. keep app widgets disabled
     disableCartButtons / enableCartButtons blanket toggle every input and
     button inside cart-drawer on every mutation, so an app widget's
     deliberately disabled button is switched back on after each update. */
  function restoreKeptDisabled() {
    document.querySelectorAll('[data-mb-keep-disabled]').forEach(function (el) {
      el.setAttribute('disabled', 'disabled');
    });
  }

  /* ------------------------------------------- 10. the drawer opens on intent only
     The theme opens the drawer on theme:product:added, and build() fires that
     after EVERY cart rebuild: an add, but also any theme:cart:refresh (the cart
     deck's add, the undo above, the generic app events in 4, a bundle). The old
     guard in mb-quick-add.js was one global flag, cleared by the first rebuild
     after a sheet add, so overlapping adds, a slow answer and any refresh all
     opened the drawer over a browsing page (measured 2026-09-23).

     Now the drawer opens from a rebuild only when the shopper asked for it:
       - an add whose button is not in a quick-add sheet (the product page's own
         form and its one-tap buttons), or a sheet launched from inside the
         drawer (mb-quick-add.js stamps data-mb-launch on the sheet);
       - mb:cart-drawer:intent, for our own code that adds without a button
         (mb-bundle.js).
     The header icon and window.theme.cartDrawer.open() never go through a
     rebuild, so they are untouched.

     The decision is taken in a WINDOW CAPTURE listener, which runs before every
     document listener whatever order they were registered in, and read by a
     wrapper on the drawer's openCartDrawer, which the theme's product-added
     handler looks up on the instance at call time. */
  var INTENT_MS = 30000;
  var intentUntil = 0;
  var holdRebuildOpen = false;

  function grantIntent() { intentUntil = Date.now() + INTENT_MS; }
  function clearIntent() { intentUntil = 0; }

  function guardOpen(target) {
    if (!target || typeof target.openCartDrawer !== 'function') return;
    /* own property only: the instance would otherwise inherit the prototype's mark */
    if (Object.prototype.hasOwnProperty.call(target, '__mbcIntentGuard')) return;
    var original = target.openCartDrawer;
    target.openCartDrawer = function () {
      if (holdRebuildOpen) return;
      return original.apply(this, arguments);
    };
    target.__mbcIntentGuard = true;
  }

  /* the instance (its constructor bound its own copy) and the prototype (for any
     drawer constructed later, whose constructor binds from it) */
  function guardDrawerOpen() {
    var el = drawer();
    guardOpen(el);
    var Klass = window.customElements && window.customElements.get('cart-drawer');
    if (Klass) guardOpen(Klass.prototype);
  }

  window.addEventListener(
    'theme:cart:add',
    function (event) {
      guardDrawerOpen();
      var button = event.detail && event.detail.button;
      var sheet = button && button.closest ? button.closest('[data-quick-add-modal]') : null;
      if (!sheet || sheet.getAttribute('data-mb-launch') === 'drawer') grantIntent();
    },
    true
  );

  document.addEventListener('mb:cart-drawer:intent', grantIntent);

  /* a product-page add that failed must not leave an intent behind for the next
     unrelated rebuild */
  window.addEventListener(
    'theme:product:add-error',
    function (event) {
      var button = event.target;
      if (button && button.closest && !button.closest('[data-quick-add-modal]')) clearIntent();
    },
    true
  );

  window.addEventListener(
    'theme:product:added',
    function () {
      holdRebuildOpen = Date.now() >= intentUntil;
      /* released after every listener has run: the window bubble listener below,
         and a microtask in case someone stops the event's propagation */
      Promise.resolve().then(function () { holdRebuildOpen = false; });
    },
    true
  );
  window.addEventListener('theme:product:added', function () { holdRebuildOpen = false; });

  /* an intent is spent by the open it asked for */
  document.addEventListener('theme:cart-drawer:open', clearIntent);

  /* ------------------------------------------- 11. visible or closed, never between
     Contract 3 in cart-drawer.css makes both resting states static. This checks
     the result shortly after every change of state, and again whenever the page
     comes back (bfcache restore, app switch), and settles anything in between:
       - open but not showing: finish the stalled animation; if it still is not
         on screen, close it, so the page is never locked under a panel nobody
         can see;
       - open with no animation at all (animation:none): the theme waits for an
         animationend that will never come before it traps focus, so send it;
       - is-closing that never ends: same event, then drop the class;
       - closed but still marked open, or the page still scroll-locked with no
         overlay left: release it. */
  var WATCH_MS = 700;
  var watchTimer = 0;

  function panel() {
    var el = drawer();
    return el ? el.querySelector('[data-cart-drawer-inner]') : null;
  }

  function showing(node) {
    var cs = getComputedStyle(node);
    if (cs.visibility !== 'visible' || parseFloat(cs.opacity) < 0.9) return false;
    var r = node.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 1 && r.left < window.innerWidth - 1 && r.bottom > 1 && r.top < window.innerHeight - 1;
  }

  function animationsOf(node) {
    return node && typeof node.getAnimations === 'function' ? node.getAnimations() : [];
  }

  function finishAnimations(node) {
    animationsOf(node).forEach(function (a) {
      try { a.finish(); } catch (e) {}
    });
  }

  /* what the theme's waitForAnimationEnd listens for: target === the panel */
  function sendAnimationEnd(node) {
    try { node.dispatchEvent(new Event('animationend')); } catch (e) {}
  }

  function otherOverlayOpen() {
    if (document.querySelector('.drawer.is-open:not(cart-drawer)')) return true;
    return !!document.querySelector('dialog[open]:not(.mb-gone):not(.mb-closing)');
  }

  /* WebKit can leave a composited fixed layer unpainted after a class-driven
     animation until something else invalidates it. A no-op paint change is that
     something. */
  function nudgePaint(node) {
    node.style.outline = '1px solid transparent';
    setTimeout(function () { node.style.outline = ''; }, 60);
  }

  function settleDrawer() {
    watchTimer = 0;
    var el = drawer();
    var inner = panel();
    if (!el || !inner) return;
    var open = el.classList.contains('is-open');

    if (el.classList.contains('is-closing') && !open) {
      if (!animationsOf(inner).some(function (a) { return a.playState === 'running'; })) {
        sendAnimationEnd(inner);
        if (el.classList.contains('is-closing')) el.classList.remove('is-closing');
      }
      return;
    }

    if (open) {
      if (!animationsOf(inner).length) sendAnimationEnd(inner);
      if (!showing(inner)) {
        finishAnimations(inner);
        if (!showing(inner)) {
          if (window.console) console.warn('[cart-drawer] open but not on screen; closing it');
          if (typeof el.closeCartDrawer === 'function') el.closeCartDrawer();
          scheduleSettle();
          return;
        }
      }
      nudgePaint(inner);
      syncDialog(true);
      return;
    }

    /* closed */
    syncDialog(false);
    if (el.cartDrawerIsOpen) el.cartDrawerIsOpen = false;
    /* The theme's lock is deferred a tick and its unlock is not, so a close and
       an open in one task end locked with nothing open. Only straight after the
       drawer itself closed, which is when the theme unlocks unconditionally
       anyway, so a filter panel or menu that holds its own lock is left alone. */
    if (justClosed) {
      justClosed = false;
      if (document.documentElement.hasAttribute('data-scroll-locked') && !otherOverlayOpen()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }
    }
  }

  function scheduleSettle() {
    clearTimeout(watchTimer);
    watchTimer = setTimeout(settleDrawer, WATCH_MS);
  }

  var watchedHost = null;
  var wasOpen = false;
  var justClosed = false;
  var hostObserver = new MutationObserver(function () {
    var el = drawer();
    if (!el) return;
    var nowOpen = el.classList.contains('is-open');
    if (wasOpen && !nowOpen) justClosed = true;
    wasOpen = nowOpen;
    /* follow the class at once for [inert]; settle after the motion */
    syncDialog(nowOpen);
    scheduleSettle();
  });

  function watchDrawer() {
    var el = drawer();
    if (!el || el === watchedHost) return;
    if (watchedHost) hostObserver.disconnect();
    watchedHost = el;
    wasOpen = el.classList.contains('is-open');
    hostObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) settleDrawer();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleSettle();
  });

  /* ------------------------------------------- 12. a new visitor's first adds go one at a time
     A visitor who has never added anything has no cart yet: Shopify creates it
     on the first add and answers with the `cart` cookie. Two adds sent before
     that answer each find no cart, so each creates its own, and the browser
     keeps whichever cookie came last: one product is silently lost while the
     page said "added" twice (measured 2026-09-23: both requests 200, one line).

     So while there is no cart cookie, adds to /cart/add queue behind the one in
     flight and are sent as soon as it answers. The caller just sees a slower
     answer: the theme's button stays in its loading state and the quick-add
     toast keeps saying "adding". Once the cookie exists every add goes straight
     out, exactly as before. Wraps fetch, which the theme, the cart deck and
     mb-bundle.js all use, and looks it up at call time, so wrapping it here
     reaches them all. */
  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;
  var addQueue = null;

  function hasCart() {
    return /(?:^|;\s*)cart=/.test(document.cookie);
  }

  if (typeof window.fetch === 'function' && !window.fetch.__mbcAddQueue) {
    var nativeFetch = window.fetch;
    var queuedFetch = function (input, init) {
      var args = arguments;
      var send = function () {
        return nativeFetch.apply(window, args);
      };
      var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      var method = String((init && init.method) || (input && typeof input === 'object' && input.method) || 'GET').toUpperCase();
      if (method !== 'POST' || !ADD_URL.test(url.split('#')[0])) return send();
      if (!addQueue && hasCart()) return send();

      var result = addQueue ? addQueue.then(send, send) : send();
      var settled = result.then(function () {}, function () {});
      addQueue = settled;
      settled.then(function () {
        if (addQueue === settled) addQueue = null;
      });
      return result;
    };
    queuedFetch.__mbcAddQueue = true;
    window.fetch = queuedFetch;
  }

  /* ---------------------------------------------------------------- boot */
  function boot() {
    wireApi();
    guardDrawerOpen();
    watchDrawer();
    patchUpdateCart();
    var open = !!(drawer() && drawer().classList.contains('is-open'));
    syncToggles(open);
    syncDialog(open);
    mirrorDisabled();
    observeQty();
    if (open) startMascot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* the custom elements may upgrade after us */
  window.addEventListener('load', boot);
  document.addEventListener('theme:cart:change', patchUpdateCart);
})();
